import { useEffect, useId, type ReactElement, type ReactNode } from "react";
import { BODY_TYPE } from "odyssey-shared";
import { Button } from "./Button.tsx";
import { CentredRow } from "./Centred.tsx";
import { Dialog } from "./Dialog.tsx";
import { Glyph } from "./Glyph.tsx";
import { KeyGlyph } from "./KeyGlyph.tsx";
import { KIT_METRICS, centreOffset, logical } from "./metrics.ts";

/**
 * A confirm dialog (P1.4-T15): a `Dialog` that asks before an action the player cannot take back. Its body
 * names the consequence and is the dialog's accessible description (`describedBy`), so assistive technology
 * reads it with the title as the dialog opens; its actions are Cancel on the left and the confirm button, in
 * the danger variant, on the right (the author's ruling of 2026-09-27). It opens with focus on the confirm
 * button, so Enter confirms and Escape cancels (the author's ruling of 2026-09-26 on R-0042, P1.6-T27): the
 * one place a danger button takes a dialog's first focus, moved there once the Dialog has focused its first safe control. Enter on Cancel reached by Tab, and Escape,
 * cancel; like every dialog with actions it is centred and has no close button (ADR-061's amendment "the kit
 * and the entry forms match the design"). Under its actions a row of key hints says so, and its title is in the danger colour,
 * as the design draws it.
 *
 * While the page runs the confirmed action it marks the dialog busy: the confirm button shows its dots and
 * refuses every activation, Cancel is disabled, and a request to close (Escape) is refused, so the dialog
 * keeps its router layer and the next Escape asks again rather than reaching the game behind it. The action
 * cannot be called back once sent, which is why nothing cancels it. A failure is shown on the error line
 * under the body, led by the 8 px alert glyph, which takes space only when there is one and stays on the
 * page, empty and with no height, until then, since it is a polite live region and assistive technology
 * reads a sentence that appears in one already there.
 */

/**
 * The dialog's width in logical pixels unless the page gives one.
 */
const WIDTH = 208;

/**
 * The key hint row's words, each after its key glyph, as the design writes them ("Enter to confirm", "Esc to
 * cancel"; ADR-061's amendment "the game menu, character select, loading and confirm dialog match the design"),
 * drafts for the author. Each continues the key's name, so it starts in lower case and ends with no full stop.
 */
export const CONFIRM_HINT_WORDING = { confirmKey: "Enter", confirm: "to confirm", cancelKey: "Esc", cancel: "to cancel" } as const;

/**
 * The key hint row under the actions, as the design draws it: the Enter key glyph and "to confirm", then the Esc
 * key glyph and "to cancel", each word set in the middle of the key glyph's height and as wide as its text
 * rounded up to a whole logical pixel, so the glyphs after it stay on the grid. It repeats what the confirm
 * button's first focus and Escape already do, so it is hidden from assistive technology, as a menu item's key
 * hint is.
 *
 * @returns The row's content.
 */
function KeyHints(): ReactElement {
  const words = { marginTop: logical(centreOffset(KIT_METRICS.key.height, BODY_TYPE.caption.lineHeight)) };
  return (
    <span className="ot-confirm__hints" data-kit="confirm-hints" aria-hidden="true" style={{ gap: logical(KIT_METRICS.dialog.hintItemGap) }}>
      <KeyGlyph>{CONFIRM_HINT_WORDING.confirmKey}</KeyGlyph>
      <span className="ot-confirm__hint ot-centred__text" style={words}>
        {CONFIRM_HINT_WORDING.confirm}
      </span>
      <KeyGlyph>{CONFIRM_HINT_WORDING.cancelKey}</KeyGlyph>
      <span className="ot-confirm__hint ot-centred__text" style={words}>
        {CONFIRM_HINT_WORDING.cancel}
      </span>
    </span>
  );
}

/**
 * Does nothing: what a busy dialog does with a request to close.
 */
function refuse(): void {
  return undefined;
}

/**
 * A confirm dialog.
 *
 * @param props The `title`; the body naming the consequence; the `confirmLabel` and `cancelLabel` (Cancel
 *   unless given); `onConfirm`, called when the confirm button is activated; `onCancel`, called by Cancel and
 *   Escape while the dialog is not busy; `busy` while the confirmed action runs; the
 *   `error` sentence when it failed; the `width` in logical pixels; and the `tone`, `danger` unless given, for an
 *   action that ends something (its title in the danger colour and a danger confirm button), or `primary` for one
 *   that ends nothing, such as Change character (a plain title and the primary confirm button, as the design draws
 *   its `switch` state). Either way it opens with focus on the confirm button and its focus ring shown, as the design
 *   draws it, however the dialog was opened.
 * @returns The dialog over its scrim.
 * @throws Error when mounted outside a `KitInputProvider`.
 * @example
 * <ConfirmDialog title="Delete the note" confirmLabel="Delete" onConfirm={remove} onCancel={close} busy={sending} error={failure}>
 *   The note is gone for good.
 * </ConfirmDialog>
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  busy = false,
  error,
  width = WIDTH,
  tone = "danger",
}: {
  readonly title: string;
  readonly children: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly busy?: boolean;
  readonly error?: string | undefined;
  readonly width?: number;
  readonly tone?: "danger" | "primary";
}): ReactElement {
  const note = KIT_METRICS.note;
  const bodyId = useId();
  useEffect(() => {
    document.getElementById(bodyId)?.closest("[data-kit='dialog']")?.querySelector<HTMLElement>("[data-kit='dialog-actions'] button:last-of-type")?.focus({ focusVisible: true });
  }, [bodyId]);
  return (
    <Dialog
      title={title}
      width={width}
      onClose={busy ? refuse : onCancel}
      describedBy={bodyId}
      hint={<KeyHints />}
      tone={tone === "danger" ? "danger" : "default"}
      actions={
        <>
          <Button disabled={busy} onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={tone} busy={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div id={bodyId} data-kit="confirm-body">
        {children}
      </div>
      <div
        data-kit="confirm-error"
        aria-live="polite"
        style={{
          fontSize: "calc(var(--ot-type-caption) * var(--px))",
          lineHeight: "calc(var(--ot-lh-caption) * var(--px))",
          color: "var(--ot-danger)",
          ...(error === undefined ? {} : { minHeight: logical(note.height), marginTop: logical(KIT_METRICS.field.noteGap) }),
        }}
      >
        {error === undefined ? null : (
          <CentredRow style={{ alignItems: "flex-start", gap: logical(note.gap) }}>
            <span style={{ display: "block", flex: "none", marginTop: logical(note.glyphOffset) }}>
              <Glyph name="alert" />
            </span>
            <span className="ot-centred__text" style={{ marginTop: logical(note.textOffset) }}>{error}</span>
          </CentredRow>
        )}
      </div>
    </Dialog>
  );
}
